import React, { useCallback } from 'react';
import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';
import { makeStyles } from 'tss-react/mui';
import {
  Card,
  AppBar,
  Toolbar,
  Typography,
  IconButton,
  Tooltip,
  Fade,
  Button,
} from '@mui/material';

import { useHashLocation } from 'wouter/use-hash-location';
import { useLocation } from 'wouter';
import { useAlert } from 'src/hooks';

import SaveIcon from '@mui/icons-material/Save';

import Selector from 'src/components/common/Selector';
import { REQUEST_CONFIG, REQUEST_RESULTS } from '../MassActionEditor';

import { useLoader } from 'src/reducers/loader';
import {
  useCheckEmptyFieldSelection,
  useSaveMassAction,
} from 'src/components/massAction/state/massAction';

const useStyles = makeStyles()(theme => ({
  appBar: {
    position: 'relative',
    textAlign: 'left',
  },
  primary: { color: theme.palette.primary.contrastText },
  flex: { flex: 1 },
}));

const AppBarRender = ({ actionName, save, isWritable, currentMassActionId }) => {
  const { classes } = useStyles();
  const { t } = useTranslation();
  return (
    <AppBar className={classes.appBar} color="primary">
      <Toolbar style={{ paddingLeft: '8px' }}>
        <div style={{ flex: 1 }}>
          <Typography variant="h5" color="inherit" className={classes.flex}>
            {t(`smartmessaging.massAction.columnsSelection.title`)}
          </Typography>
          <Typography variant="body2" color="inherit">
            {actionName}
            {!!currentMassActionId && ` (${currentMassActionId})`}
          </Typography>
        </div>
        {isWritable && (
          <div
            style={{
              boxShadow: 'inset 0.3px 0px 0px 0px #ffffffd5, inset -0.3px 0px 0px 0px #ffffffd5',
              marginLeft: '8px',
              marginRight: '8px',
              padding: '0 8px',
            }}
          >
            <Tooltip title={t('smartmessaging.massAction.save')}>
              <IconButton
                aria-label="clear"
                className={classes.primary}
                onClick={e => {
                  e.preventDefault();
                  save();
                }}
                size="large"
              >
                <SaveIcon />
              </IconButton>
            </Tooltip>
          </div>
        )}
      </Toolbar>
    </AppBar>
  );
};

AppBarRender.propTypes = {
  actionName: PropTypes.string.isRequired,
  save: PropTypes.func.isRequired,
  isWritable: PropTypes.bool.isRequired,
  currentMassActionId: PropTypes.number,
};

AppBarRender.defaultProps = { currentMassActionId: null };

function ColumnsSelector({
  availableResultfields = {},
  selectedResultfields,
  goToStep,
  currentName: actionName,
  isWritable,
  currentMassActionId,
  setMassActionState,
}) {
  const [hLocation, hNavigate] = useHashLocation();
  const [location] = useLocation();
  const { showAlert } = useAlert();
  const { t } = useTranslation();
  const setLoader = useLoader();
  const checkEmptyFieldSelection = useCheckEmptyFieldSelection();
  const saveMassAction = useSaveMassAction();

  const addResultFields = (group, fields) => {
    const newSelection = {
      ...selectedResultfields,
      [group]: selectedResultfields[group] ? [...selectedResultfields[group], ...fields] : fields,
    };
    setMassActionState(state => ({ ...state, selectedResultfields: newSelection }));
  };

  const removeResultFields = (group, fields) => {
    const newSelection = {
      ...selectedResultfields,
      [group]: selectedResultfields[group].filter(
        selectedField => fields.map(f => f.value).indexOf(selectedField.value) === -1
      ),
    };
    setMassActionState(state => ({ ...state, selectedResultfields: newSelection }));
  };

  const selectAll = (group, fields) => {
    const newSelection = {
      ...selectedResultfields,
      [group]: fields,
    };
    setMassActionState(state => ({ ...state, selectedResultfields: newSelection }));
  };

  const clearAll = group => {
    const newSelection = {
      ...selectedResultfields,
      [group]: [],
    };
    setMassActionState(state => ({ ...state, selectedResultfields: newSelection }));
  };

  const getAndDisplayResult = useCallback(() => {
    setLoader(true);
    saveMassAction(showAlert)
      .then(() => {
        setLoader(false);
        goToStep(REQUEST_RESULTS);
      })
      .finally(() => setLoader(false));
  }, [setLoader, saveMassAction, showAlert, goToStep]);

  const save = useCallback(() => {
    setLoader(true);
    saveMassAction(showAlert)
      .then(() => {
        setLoader(false);
      })
      .finally(() => setLoader(false));
  }, [saveMassAction, setLoader, showAlert]);

  if (!!currentMassActionId && location === '/oneoff/create' && hLocation === '/') {
    hNavigate(`${currentMassActionId}`);
    return null;
  }

  return (
    <div
      style={{
        display: 'flex',
        height: '100%',
        flexDirection: 'column',
      }}
    >
      <AppBarRender
        goToStep={goToStep}
        actionName={actionName}
        currentMassActionId={currentMassActionId}
        save={save}
        isWritable={isWritable}
      />
      <Fade in timeout={500}>
        <Card
          elevation={0}
          style={{
            display: 'flex',
            flex: 1,
          }}
        >
          {Object.keys(availableResultfields).map(
            group =>
              (availableResultfields[group].length && (
                <div
                  key={group}
                  style={{
                    overflow: 'auto',
                    margin: '4px 12px',
                    display: 'flex',
                    flexDirection: 'column',
                  }}
                >
                  <Selector
                    disabled={!isWritable}
                    selectable={availableResultfields[group]}
                    selection={selectedResultfields[group] || []}
                    select={fields => addResultFields(group, fields)}
                    unselect={fields => removeResultFields(group, fields)}
                    selectAll={() => selectAll(group, availableResultfields[group])}
                    clearAll={() => clearAll(group)}
                    title={t(`smartmessaging.massAction.columnSelector.columnGroupName.${group}`)}
                  />
                </div>
              )) ||
              null
          )}
        </Card>
      </Fade>
      <div style={{ textAlign: 'right', margin: '8px' }}>
        <Button
          variant="text"
          color="primary"
          style={{ margin: '2px' }}
          onClick={e => {
            e.preventDefault();
            goToStep(REQUEST_CONFIG);
          }}
        >
          {t('smartmessaging.buttonLabel.previous')}
        </Button>
        <Button
          variant="contained"
          color="primary"
          style={{ margin: '2px' }}
          onClick={e => {
            e.preventDefault();
            checkEmptyFieldSelection(getAndDisplayResult);
          }}
        >
          {t('smartmessaging.buttonLabel.next')}
        </Button>
      </div>
    </div>
  );
}

ColumnsSelector.propTypes = {
  goToStep: PropTypes.func.isRequired,
  classes: PropTypes.object.isRequired,
};

ColumnsSelector.defaultProps = {
  selectedResultfields: {},
  currentMassActionId: null,
};

export default ColumnsSelector;
